import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, FolderHeart, CalendarDays, Tag } from 'lucide-react';
import { client } from '@/lib/api';
import { createProjectSchema, type CreateProjectInput } from '@everplan/shared';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';


function formatWeddingDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr.slice(0, 10) + 'T00:00:00');
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function ProjectsPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: projects = [], isLoading, isError } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const res = await client.api.projects.$get();
      if (!res.ok) throw new Error('Failed to fetch projects');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateProjectInput) => {
      const res = await client.api.projects.$post({ json: data });
      if (!res.ok) throw new Error('Failed to create project');
      return res.json();
    },
    onSuccess: (newProject) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setShowCreateForm(false);
      reset();
      if (newProject?.id) {
        navigate(`/projects/${newProject.id}`);
      }
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateProjectInput>({
    resolver: zodResolver(createProjectSchema),
  });

  const onSubmit = (data: CreateProjectInput) => {
    createMutation.mutate(data);
  };

  return (
    <div className="space-y-10">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="display-tight text-3xl font-semibold">Your Projects</h1>
        <Button
          onClick={() => {
            setShowCreateForm((v) => !v);
            if (showCreateForm) reset();
          }}
        >
          <Plus className="size-4" />
          New Project
        </Button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <Card className="rounded-3xl shadow-ambient">
          <CardHeader>
            <CardTitle className="display-tight text-lg">Create a New Project</CardTitle>
            <CardDescription>Fill in the details for your wedding plan.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="name">
                  <span className="label-caps text-xs text-muted-foreground">Project Name *</span>
                </Label>
                <Input
                  id="name"
                  placeholder="e.g. Smith & Jones Wedding"
                  {...register('name')}
                  aria-invalid={!!errors.name}
                />
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                )}
              </div>

              <fieldset className="space-y-4">
                <legend className="label-caps text-xs text-muted-foreground">Person 1</legend>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="person1Name">
                      <span className="text-xs text-muted-foreground">Full Name</span>
                    </Label>
                    <Input id="person1Name" placeholder="e.g. Alex Smith" {...register('person1Name')} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="person1Email">
                      <span className="text-xs text-muted-foreground">Email</span>
                    </Label>
                    <Input id="person1Email" type="email" placeholder="alex@example.com" {...register('person1Email')} />
                    {errors.person1Email && (
                      <p className="text-xs text-destructive">{errors.person1Email.message}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="person1Phone">
                      <span className="text-xs text-muted-foreground">Phone</span>
                    </Label>
                    <Input id="person1Phone" type="tel" placeholder="(555) 123-4567" {...register('person1Phone')} />
                  </div>
                </div>
              </fieldset>

              <fieldset className="space-y-4">
                <legend className="label-caps text-xs text-muted-foreground">Person 2</legend>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="person2Name">
                      <span className="text-xs text-muted-foreground">Full Name</span>
                    </Label>
                    <Input id="person2Name" placeholder="e.g. Jordan Jones" {...register('person2Name')} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="person2Email">
                      <span className="text-xs text-muted-foreground">Email</span>
                    </Label>
                    <Input id="person2Email" type="email" placeholder="jordan@example.com" {...register('person2Email')} />
                    {errors.person2Email && (
                      <p className="text-xs text-destructive">{errors.person2Email.message}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="person2Phone">
                      <span className="text-xs text-muted-foreground">Phone</span>
                    </Label>
                    <Input id="person2Phone" type="tel" placeholder="(555) 987-6543" {...register('person2Phone')} />
                  </div>
                </div>
              </fieldset>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="weddingDate">
                    <span className="label-caps text-xs text-muted-foreground">Wedding Date</span>
                  </Label>
                  <Input
                    id="weddingDate"
                    type="date"
                    {...register('weddingDate')}
                    aria-invalid={!!errors.weddingDate}
                  />
                  {errors.weddingDate && (
                    <p className="text-xs text-destructive">{errors.weddingDate.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="guestCount">
                    <span className="label-caps text-xs text-muted-foreground">Guest Count</span>
                  </Label>
                  <Input
                    id="guestCount"
                    type="number"
                    min="1"
                    placeholder="e.g. 150"
                    {...register('guestCount', { valueAsNumber: true })}
                    aria-invalid={!!errors.guestCount}
                  />
                  {errors.guestCount && (
                    <p className="text-xs text-destructive">{errors.guestCount.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="venueName">
                    <span className="label-caps text-xs text-muted-foreground">Wedding Venue</span>
                  </Label>
                  <Input id="venueName" placeholder="e.g. The Grand Ballroom" {...register('venueName')} />
                </div>
              </div>

              {createMutation.isError && (
                <p className="text-sm text-destructive">
                  Something went wrong. Please try again.
                </p>
              )}

              <div className="flex gap-3 pt-1">
                <Button type="submit" disabled={isSubmitting || createMutation.isPending}>
                  {createMutation.isPending ? 'Creating…' : 'Create Project'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateForm(false);
                    reset();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {!showCreateForm && isLoading && (
        <p className="display-tight text-lg text-muted-foreground">Loading projects…</p>
      )}

      {/* Error state */}
      {!showCreateForm && isError && (
        <Card className="rounded-3xl shadow-ambient border-destructive/20">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">Failed to load projects. Please refresh.</p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!showCreateForm && !isLoading && !isError && projects.length === 0 && (
        <Card className="rounded-3xl bg-card shadow-ambient">
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <FolderHeart className="size-12 text-muted-foreground/40" />
            <div>
              <p className="display-tight text-lg font-medium">No projects yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Create your first wedding project to get started.
              </p>
            </div>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="size-4" />
              New Project
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Project grid */}
      {!showCreateForm && !isLoading && !isError && projects.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="group block rounded-3xl bg-card shadow-ambient transition-shadow hover:shadow-[0_16px_40px_rgba(49,51,47,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring overflow-hidden"
            >
              {project.photoUrl && (
                <img src={`/api/files/${project.photoUrl}`} alt={project.name} className="w-full h-36 object-cover" />
              )}
              <div className="p-8">
                <p className="label-caps text-xs text-muted-foreground">Wedding Project</p>
                <h3 className="mt-2 text-lg font-semibold leading-snug group-hover:text-primary transition-colors">
                  {project.name}
                </h3>

                {(project.person1Name || project.person2Name) && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {[project.person1Name, project.person2Name].filter(Boolean).join(' & ')}
                  </p>
                )}

                <div className="mt-5 flex flex-wrap gap-4">
                  <div className="flex items-center gap-1.5">
                    <Tag className="size-3.5 text-muted-foreground/60" />
                    <span className="label-caps text-xs text-muted-foreground">
                      {project.categories.length}{' '}
                      {project.categories.length === 1 ? 'category' : 'categories'}
                    </span>
                  </div>

                  {project.weddingDate && (
                    <div className="flex items-center gap-1.5">
                      <CalendarDays className="size-3.5 text-muted-foreground/60" />
                      <span className="label-caps text-xs text-muted-foreground">
                        {formatWeddingDate(project.weddingDate)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
